import reactUpdate, {Spec} from 'immutability-helper';
import $ from 'jquery';

import FileBrowserActions from 'file-browser/FileBrowserActions';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  FromServerSubtemplate,
  FromServerSubtemplateVersion,
  Subtemplate,
  SubtemplateVersion,
  ToServerSubtemplate,
  ToServerSubtemplateVersion,
} from 'notebook/entry-templates/TemplateConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {BioentityNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import pick from 'util/pick';
import {asJQueryPromise} from 'util/Promises';
import uniqueId from 'util/uniqueId';

import SubtemplateDAO from './SubtemplateDAO';

// Subtemplates don't have days, so we can use BioentityNote as a type alias.
type SubtemplateNote = BioentityNote;

const SubtemplateUtil = {
  /**
   * Transforms the given `op` to operate on the noteVersion of a subtemplate.
   */
  noteVersionUpdate(op: Spec<SubtemplateNote>): Spec<Subtemplate> {
    return {subtemplateVersion: {noteVersion: op}};
  },

  pickRevertableFields(subtemplate: Subtemplate): {name: string; subtemplateVersion: SubtemplateVersion} {
    return pick(subtemplate, 'name', 'subtemplateVersion');
  },

  sanitizeSubtemplate(subtemplate: Subtemplate, oldSubtemplate: Subtemplate | null = null): Subtemplate {
    return reactUpdate(
      subtemplate,
      SubtemplateUtil.noteVersionUpdate({
        $apply(noteVersion: SubtemplateNote): SubtemplateNote {
          return NoteStoreTransforms.sanitizeNote(
            noteVersion,
            oldSubtemplate != null ? oldSubtemplate.subtemplateVersion.noteVersion : undefined
          );
        },
      })
    );
  },

  prepareForStore(subtemplate: FromServerSubtemplate, cidIdMap: CidIdMap): Subtemplate {
    const {
      id,
      name,
      creator,
      creatorId,
      templateCollection,
      editURL,
      permissions,
      latestVersionId,
      archiveRecord,
      canViewLatestVersion,
      review,
    } = subtemplate;
    const {createdAt, modifiedAt} = subtemplate.subtemplateVersion;
    return {
      id,
      name,
      templateCollection,
      editURL,
      permissions,
      latestVersionId,
      archiveRecord,
      canViewLatestVersion,
      creator,
      creatorId,
      createdAt,
      modifiedAt,
      review,
      subtemplateVersion: SubtemplateUtil.prepareSTVForStore(id, subtemplate.subtemplateVersion, cidIdMap),
    };
  },

  prepareSTVForStore(
    fileId: string,
    subtemplateVersion: FromServerSubtemplateVersion,
    cidIdMap: CidIdMap
  ): {
    cid: string;
    noteVersion: SubtemplateNote;
    apiId: string;
  } {
    const cid = uniqueId('tv');
    cidIdMap[cid] = subtemplateVersion.id;
    return {
      cid,
      noteVersion: NoteStoreTransforms.prepareForStore(
        subtemplateVersion.noteVersion,
        cidIdMap,
        fileId
      ) as SubtemplateNote,
      apiId: subtemplateVersion.api_identifier,
    };
  },

  prepareSTVForSave(
    subtemplateVersion: SubtemplateVersion,
    cidIdMap: CidIdMap,
    fileId: string
  ): ToServerSubtemplateVersion {
    return {
      id: cidIdMap[subtemplateVersion.cid] as number,
      cid: subtemplateVersion.cid,
      noteVersion: NoteStoreTransforms.prepareForSave(subtemplateVersion.noteVersion, cidIdMap, fileId),
    };
  },

  prepareForSave(subtemplate: Subtemplate, cidIdMap: CidIdMap): ToServerSubtemplate {
    return {
      name: subtemplate.name,
      subtemplateVersion: SubtemplateUtil.prepareSTVForSave(
        subtemplate.subtemplateVersion,
        cidIdMap,
        subtemplate.id
      ),
    };
  },

  createThenOpen(name: string, templateCollectionId: string): JQueryPromise<void> {
    $('#fileLoading').removeClass('hidden');
    return asJQueryPromise(SubtemplateDAO.create({name, templateCollectionId}))
      .always(() => {
        $('#fileLoading').addClass('hidden');
      })
      .done((subtemplateResponse: FromServerSubtemplate) => {
        writeFragmentsToCache(getFragmentWrapper('SubtemplateSummary'), [
          {
            __typename: 'SubtemplateNode',
            ...subtemplateResponse.subtemplateVersion.subtemplate,
            templateCollection: {
              __typename: 'TemplateCollectionNode',
              ...subtemplateResponse.subtemplateVersion.subtemplate.templateCollection,
            },
            editUrl: subtemplateResponse.subtemplateVersion.subtemplate.editURL,
            archiveRecord: null,
            creator: {
              __typename: 'UserNode',
              id: subtemplateResponse.creatorId,
              name: subtemplateResponse.creator,
            },
          },
        ]);
        FileBrowserActions.openFile(subtemplateResponse.id, {
          folderItem: {id: subtemplateResponse.id, name: subtemplateResponse.name},
          toolbarTrigger: 'openInformationPanel',
        });
      })
      .fail(notifications.notifyError);
  },
};

export default SubtemplateUtil;
